import { Contract, ethers } from 'ethers'
import { useVvsBar } from 'hooks/useContract'
import { useCallback } from 'react'
import { useCallWithGasPrice } from 'hooks/useCallWithGasPrice'
import { TransactionResponse } from '@ethersproject/providers'
import { DEFAULT_GAS_LIMIT, DEFAULT_TOKEN_DECIMAL } from 'config'
import getGasPrice from 'utils/getGasPrice'
import BigNumber from 'bignumber.js'
import { parseUnits } from 'ethers/lib/utils'

const options = {
  gasLimit: DEFAULT_GAS_LIMIT,
}

const enterPool = async (vvsBar, amount) => {
  const gasPrice = getGasPrice()
  const tx = await vvsBar.enterPool(new BigNumber(amount).times(DEFAULT_TOKEN_DECIMAL).toString(), {
    ...options,
    gasPrice,
  })
  const receipt = await tx.wait()
  return {
    ...receipt,
    hash: receipt.transactionHash,
  }
}

const leavePool = async (vvsBar, amount) => {
  const gasPrice = getGasPrice()
  const units = parseUnits(amount, 18)

  const tx = await vvsBar.leavePool(units.toString(), {
    gasPrice,
  })
  const receipt = await tx.wait()
  return {
    ...receipt,
    hash: receipt.transactionHash,
  }
}

const useVvsBarHandlers = () => {
  const vvsBar = useVvsBar()
  const { callWithGasPrice } = useCallWithGasPrice()

  const handleApprove = useCallback(
    async (ERC20: Contract) => {
      try {
        const tx = await callWithGasPrice(ERC20, 'approve', [vvsBar.address, ethers.constants.MaxUint256])
        const receipt = await tx.wait()
        return receipt.status
      } catch (e) {
        return false
      }
    },
    [vvsBar, callWithGasPrice],
  )

  const handleEnterPool = useCallback(
    async (amount: string): Promise<TransactionResponse> => {
      return enterPool(vvsBar, amount)
    },
    [vvsBar],
  )

  const handleLeavePool = useCallback(
    async (amount: string): Promise<TransactionResponse> => {
      return leavePool(vvsBar, amount)
    },
    [vvsBar],
  )

  return {
    onApprove: handleApprove,
    onEnterPool: handleEnterPool,
    onLeavePool: handleLeavePool,
  }
}

export default useVvsBarHandlers
